/* ═══════════════════════════════════════════════════════════════════════════
   Allocateur de slots à capacité fixe — un « sparse set ».

   Ce que ça donne, et pourquoi c'est la bonne structure ici :
     - alloc() et free() en O(1), sans recherche
     - itération DENSE : aucun trou, donc aucun test `if (alive[i])` dans la
       boucle chaude, et un parcours mémoire linéaire
     - zéro allocation après la construction
     - les identifiants de slot restent stables tant que l'entité vit

   L'astuce : `dense` est en permanence une PERMUTATION de tous les slots.
   Les `count` premiers sont vivants, le reste est libre. `sparse` est sa
   fonction inverse. L'invariant `dense[sparse[s]] === s` tient toujours, ce
   qui rend `isAlive` aussi simple qu'une comparaison d'entiers.

   C'est ce que le plan appelle « pools SoA maison, ~250 lignes » (PLAN.md §2),
   retenus contre tous les ECS JS : aucun d'entre eux n'est simultanément
   vivant, stable et mature en 2026, et un shmup à 6-8 archétypes fixes n'a
   aucun usage de la composition dynamique qui justifie un ECS.
   ═══════════════════════════════════════════════════════════════════════════ */

export class SoAPool {
  readonly capacity: number;

  /** dense[0 .. count-1] = slots vivants ; dense[count .. ] = slots libres. */
  private readonly dense: Int32Array;
  /** sparse[slot] = position de `slot` dans `dense`. */
  private readonly sparse: Int32Array;
  private n = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.dense = new Int32Array(capacity);
    this.sparse = new Int32Array(capacity);
    for (let i = 0; i < capacity; i++) {
      this.dense[i] = i;
      this.sparse[i] = i;
    }
  }

  /** Nombre d'entités vivantes. */
  get count(): number {
    return this.n;
  }

  /** Slot vivant n° i. Valide pour 0 <= i < count.
   *
   *  ⚠️ PIÈGE — sens d'itération. `free()` déplace le dernier slot vivant à la
   *  position libérée. Une boucle EN AVANT sauterait donc l'élément déplacé.
   *  Toute boucle qui peut libérer DOIT aller en arrière :
   *
   *      for (let i = pool.count - 1; i >= 0; i--) {
   *        const s = pool.at(i);
   *        ...
   *        if (mort) pool.free(s);   // sûr : ce qui remonte a déjà été vu
   *      }
   *
   *  Ce commentaire existe parce que l'erreur inverse est silencieuse : elle ne
   *  plante pas, elle fait juste qu'un projectile sur N est ignoré une frame —
   *  et on la cherche pendant des heures. */
  at(i: number): number {
    return this.dense[i]!;
  }

  isAlive(slot: number): boolean {
    return this.sparse[slot]! < this.n;
  }

  /** Retourne un slot libre, ou -1 si le pool est plein.
   *
   *  Saturer est un comportement VOULU : un pattern buggé qui émet en boucle
   *  doit dégrader le jeu, pas faire croître un tableau jusqu'à l'OOM. Les
   *  appelants comptabilisent les refus (voir World.overflow). */
  alloc(): number {
    if (this.n >= this.capacity) return -1;
    // L'invariant garantit déjà sparse[slot] === this.n.
    const slot = this.dense[this.n]!;
    this.n++;
    return slot;
  }

  /** Libère un slot. Idempotent : libérer deux fois ne corrompt rien. */
  free(slot: number): void {
    const i = this.sparse[slot]!;
    if (i >= this.n) return; // déjà libre
    const last = this.n - 1;
    const lastSlot = this.dense[last]!;

    this.dense[i] = lastSlot;
    this.sparse[lastSlot] = i;
    this.dense[last] = slot;
    this.sparse[slot] = last;

    this.n--;
  }

  /** Vide le pool sans rien réallouer. */
  clear(): void {
    this.n = 0;
  }
}
